import type { AppDispatch } from '../store/store';
import { baseApi } from './baseApi';

/**
 * SSE (Server-Sent Events) service for real-time updates
 *
 * Replaces WebSocket/Socket.io with standard HTTP SSE.
 * Works with Superhosting (no WebSocket support needed).
 *
 * Events received from the server:
 * - pallet:received, pallet:moved, pallet:picked
 * - alert:expiry, alert:low_stock, alert:critical_batch
 * - inventory:updated, system:auto_expired
 */

// Map SSE event types to RTK Query cache tags to invalidate
const EVENT_TAG_MAP: Record<string, string[]> = {
    'pallet:received': ['Pallets', 'Dashboard', 'Movements'],
    'pallet:moved': ['Pallets', 'Locations', 'Movements', 'Dashboard'],
    'pallet:picked': ['Pallets', 'Orders', 'Movements', 'Dashboard'],
    'alert:expiry': ['ExpiryAlerts', 'Dashboard'],
    'alert:low_stock': ['Products', 'Dashboard'],
    'alert:critical_batch': ['ExpiryAlerts', 'Dashboard'],
    'inventory:updated': ['Pallets', 'Products', 'Locations', 'Dashboard'],
    'system:auto_expired': ['Pallets', 'ExpiryAlerts', 'Dashboard'],
};

type SSEEventCallback = (event: string, data: Record<string, unknown>) => void;

class SSEClient {
    private eventSource: EventSource | null = null;
    private dispatch: AppDispatch | null = null;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectAttempts = 0;
    private maxReconnectDelay = 60; // seconds
    private shouldReconnect = true;
    private listeners: Map<string, SSEEventCallback[]> = new Map();

    /**
     * Initialize and connect SSE with Redux store dispatch
     */
    connect(dispatch: AppDispatch): void {
        this.dispatch = dispatch;
        this.shouldReconnect = true;
        this._connect();
    }

    private _connect(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        const baseUrl = import.meta.env.VITE_API_URL || '/api';
        const sseUrl = `${baseUrl}/events`;

        try {
            // Use EventSource with credentials to send auth cookies
            this.eventSource = new EventSource(sseUrl, {
                withCredentials: true,
            });

            this.eventSource.onopen = () => {
                console.log('[SSE] Connected');
                this.reconnectAttempts = 0;
            };

            this.eventSource.onerror = () => {
                console.warn('[SSE] Connection error');
                this.eventSource?.close();
                this.eventSource = null;
                this._scheduleReconnect();
            };

            // Listen for all known event types
            const eventTypes = Object.keys(EVENT_TAG_MAP);
            for (const eventType of eventTypes) {
                this.eventSource.addEventListener(eventType, (event) => {
                    this._handleEvent(eventType, event as MessageEvent);
                });
            }

            // Also listen for generic 'message' events (fallback)
            this.eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type) {
                        this._handleEvent(data.type, event);
                    }
                } catch {
                    // Skip unparseable messages
                }
            };
        } catch (error) {
            console.error('[SSE] Failed to create EventSource:', error);
            this._scheduleReconnect();
        }
    }

    private _handleEvent(eventType: string, event: MessageEvent): void {
        try {
            const data = JSON.parse(event.data);
            console.log(`[SSE] Event: ${eventType}`, data);

            // Invalidate RTK Query cache tags
            const tagsToInvalidate = EVENT_TAG_MAP[eventType];
            if (tagsToInvalidate && this.dispatch) {
                this.dispatch(
                    baseApi.util.invalidateTags(
                        tagsToInvalidate.map((tag) => ({ type: tag as never }))
                    )
                );
            }

            // Notify custom listeners
            const callbacks = this.listeners.get(eventType);
            if (callbacks) {
                for (const cb of callbacks) {
                    try {
                        cb(eventType, data);
                    } catch (e) {
                        console.error(`[SSE] Listener error for ${eventType}:`, e);
                    }
                }
            }

            // Notify wildcard listeners
            const wildcardCallbacks = this.listeners.get('*');
            if (wildcardCallbacks) {
                for (const cb of wildcardCallbacks) {
                    try {
                        cb(eventType, data);
                    } catch (e) {
                        console.error('[SSE] Wildcard listener error:', e);
                    }
                }
            }
        } catch (e) {
            console.error(`[SSE] Error parsing event ${eventType}:`, e);
        }
    }

    private _scheduleReconnect(): void {
        if (!this.shouldReconnect) return;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }

        const delay = Math.min(
            Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
        );
        console.log(
            `[SSE] Reconnecting in ${delay}s (attempt ${this.reconnectAttempts + 1})`
        );

        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this._connect();
        }, delay * 1000);
    }

    /**
     * Register a listener for a specific event type
     * Use '*' for wildcard (all events)
     */
    on(eventType: string, callback: SSEEventCallback): void {
        const existing = this.listeners.get(eventType) || [];
        existing.push(callback);
        this.listeners.set(eventType, existing);
    }

    /**
     * Remove a specific listener
     */
    off(eventType: string, callback: SSEEventCallback): void {
        const existing = this.listeners.get(eventType);
        if (existing) {
            this.listeners.set(
                eventType,
                existing.filter((cb) => cb !== callback)
            );
        }
    }

    /**
     * Disconnect and clean up
     */
    disconnect(): void {
        this.shouldReconnect = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        this.listeners.clear();
        this.reconnectAttempts = 0;
        console.log('[SSE] Disconnected');
    }

    /**
     * Check if connected
     */
    get isConnected(): boolean {
        return this.eventSource?.readyState === EventSource.OPEN;
    }
}

// Singleton instance
export const sseClient = new SSEClient();
